import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import updateScheduledSystemAction from '../api/updateScheduledSystemAction';
import {
  getTomorrowDateBerlin,
  isScheduledEditDirty,
  resolveScheduledMailContent,
  toScheduledDateInputValue,
} from '../utils/format';
import { htmlToPlainText } from '../../../utils/htmlPlainTextConverter';

interface Props {
  open: boolean;
  logId: number | null;
  providerName: string | null;
  scheduledAt: string | null;
  mailContent: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

// Earliest selectable day = tomorrow in EUROPE/BERLIN (getTomorrowDateBerlin), not the
// admin's browser-local calendar day. The backend anchors scheduled sends at 00:00
// Europe/Berlin and rejects a scheduled_at that is not strictly in the future, so the
// picker's lower bound must follow the Berlin calendar day regardless of the viewer's
// timezone — otherwise a browser west/east of Berlin near midnight would block a valid
// Berlin date or allow an already-past one (Audit P2; HOTSPOTS Tag-Shift class).
function validateScheduledDate(dateStr: string): string {
  if (!dateStr) return 'Bitte ein Datum wählen.';
  if (dateStr < getTomorrowDateBerlin()) return 'Termin darf nur in der Zukunft liegen.';
  return '';
}

// Edit dialog for a still-scheduled system-access mail (RITA-1317). Prefills the
// current content and planned date, lets the editor change either, and saves through
// the existing updateScheduledSystemAction mutation — which appends an append-only
// `updated` history entry and keeps it ONE log row. The parent refetches the log on
// success, so this dialog does not drive the row state itself. Mirrors the structure
// of CancelScheduledActionDialog.
function EditScheduledActionDialog({ open, logId, providerName, scheduledAt, mailContent, onClose, onSuccess }: Props) {
  const [content, setContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [updateMutation, { loading }] = useMutation(updateScheduledSystemAction);
  // The plain-text we seeded the textarea with on open. Compared on save so an UNCHANGED
  // body is persisted as the original HTML verbatim instead of the lossy plaintext→HTML
  // round-trip — see resolveScheduledMailContent (Audit/Review P1).
  const seededPlainTextRef = useRef('');
  // The date we seeded the picker with on open. Compared on save together with the body so a
  // pure no-op (neither date nor text changed) never fires the mutation — see isDirty below.
  const seededDateRef = useRef('');

  // Latest props mirrored into refs so the seeding effect can read them WITHOUT taking them as
  // dependencies. The form must be seeded ONLY when the dialog OPENS or the target ROW changes —
  // never on a later change of mailContent/scheduledAt while it is open. The panel feeds those props
  // from a `cache-and-network` query, so a background GetAdvisorSystemLog refetch (or any later
  // refreshLog) can update them mid-edit; depending on them here would re-run the effect and WIPE
  // the operator's unsaved input (Codex Review P1).
  const mailContentRef = useRef(mailContent);
  mailContentRef.current = mailContent;
  const scheduledAtRef = useRef(scheduledAt);
  scheduledAtRef.current = scheduledAt;

  // Seed from the target row when the dialog opens (open false→true) or switches to a different row
  // (logId change). The content is stored as HTML; edit it as plain text and convert back on save,
  // mirroring the compose dialog. We read the CURRENT props from refs so seeding always uses the
  // values at open time, without re-seeding on background prop refreshes.
  useEffect(() => {
    if (open) {
      const seeded = htmlToPlainText(mailContentRef.current ?? '');
      seededPlainTextRef.current = seeded;
      setContent(seeded);
      const seededDate = toScheduledDateInputValue(scheduledAtRef.current);
      seededDateRef.current = seededDate;
      setScheduledDate(seededDate);
      setSubmitError(null);
      setTouched(false);
    }
  }, [open, logId]);

  const trimmedContent = content.trim();
  const contentError = touched && !trimmedContent ? 'Bitte einen Inhalt angeben.' : null;
  const dateError = touched ? validateScheduledDate(scheduledDate) : '';
  // Block a no-op save: updateScheduledSystemAction always appends an `updated` revision, so
  // firing it without a real change inflates the audit trail (Review/Audit P1). Central helper
  // so the dirty rule is unit-tested alongside resolveScheduledMailContent (format.test.ts).
  const isDirty = isScheduledEditDirty(seededPlainTextRef.current, content, seededDateRef.current, scheduledDate);

  const handleSubmit = async () => {
    setTouched(true);
    if (!trimmedContent || validateScheduledDate(scheduledDate) || logId == null) return;
    // Belt-and-suspenders: the submit button is already disabled while !isDirty, but guard the
    // mutation itself so no code path can persist a no-op revision.
    if (!isDirty) {
      setSubmitError('Es wurden keine Änderungen vorgenommen.');
      return;
    }

    try {
      const { data } = await updateMutation({
        variables: {
          id: logId,
          input: {
            // Always send a concrete date so the row stays scheduled — the
            // "sofort senden statt terminieren" path is intentionally out of scope
            // (RITA-1317). The backend treats a null scheduled_at as immediate send.
            scheduled_at: `${scheduledDate}T00:00:00`,
            // Preserve the original HTML when the body was not touched; only convert a
            // genuinely changed plaintext body (Audit/Review P1).
            mail_content: resolveScheduledMailContent(mailContent ?? '', seededPlainTextRef.current, content),
            is_manual_edit: true,
          },
        },
      });
      if (data?.updateScheduledSystemAction?.success) {
        onSuccess();
      } else {
        setSubmitError(data?.updateScheduledSystemAction?.message || 'Aktion konnte nicht gespeichert werden.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSubmitError(message);
    }
  };

  const submitDisabled = loading || !trimmedContent || !!validateScheduledDate(scheduledDate) || !isDirty;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Geplante E-Mail bearbeiten</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Dienstleister</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{providerName || '—'}</Typography>
        </Box>

        <TextField
          fullWidth
          type="date"
          label="Geplant für"
          value={scheduledDate}
          onChange={(e) => {
            setScheduledDate(e.target.value);
            if (!touched) setTouched(true);
            if (submitError) setSubmitError(null);
          }}
          onBlur={() => setTouched(true)}
          error={!!dateError}
          helperText={dateError || 'Versand erfolgt am gewählten Tag um 00:00 Uhr (Europe/Berlin).'}
          disabled={loading}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: getTomorrowDateBerlin(), 'aria-label': 'Geplant für' }}
          sx={{ mb: 3 }}
        />

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={6}
          label="Inhalt der E-Mail"
          placeholder="Inhalt der geplanten E-Mail"
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            if (!touched) setTouched(true);
            if (submitError) setSubmitError(null);
          }}
          onBlur={() => setTouched(true)}
          error={!!contentError}
          helperText={contentError || 'Jede Bearbeitung wird im Verlauf revisionssicher gespeichert.'}
          disabled={loading}
          inputProps={{ 'aria-label': 'Inhalt der E-Mail' }}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Änderungen speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditScheduledActionDialog;
